import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { reportsApi, type ReportData } from '../../api/reports.js';
import { paymentCardApi, type CardEditBody } from '../../api/paymentCard.js';
import { ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { Spinner, ErrorBox, EmptyBox, Pagination, SearchInput, Field } from '../../components/ui.js';
import { useUrlSearch, useUrlUpdate } from '../../lib/hooks.js';
import { dateOnly, datetime, formatAmountDraft, isZeroDate, localDate, money, text } from '../../lib/format.js';

type Row = Record<string, unknown>;

const STATE_TABS = [
  { value: '0', label: '보고건 대기 · 예정건' },
  { value: '1', label: '이전내역 · 완료건' },
];

/** 서버 검증(card.schemas.ts 의 CARD_TYPES)·목록 정의(report.registry.ts)와 같은 목록이다. */
const CARD_TYPES: Record<string, string> = {
  of: '사무실 카드',
  ui: '의정부매장 카드',
  ch: '청담매장 카드',
  lc: '물류센터 카드',
};

const USE_AREAS: Record<string, string> = {
  '0': '미지정', '1': '사무실', '2': '물류센터', '3': '의정부 매장',
  '4': '마케팅', '5': '기타', '6': '청담 매장', '7': 'CS팀',
  '8': '디자인팀', '9': '기획팀', '10': '상품관리팀', '11': 'TV바나나',
};

interface CardFormState {
  corp: string;
  card_type: string;
  use_area: string;
  se_name: string;
  up_name: string;
  amountDraft: string;
  amountUsDraft: string;
  usRateDraft: string;
  cardmemo: string;
  re_name: string;
  report_date: string;
  succ_date: string;
  memo: string;
}

/** 금액 컬럼(정수)을 입력칸 초깃값("120,000")으로. 빈 값·0 은 빈 칸으로 둔다. */
function amountDraftOf(value: unknown): string {
  const n = Number(value ?? 0);
  return Number.isFinite(n) && n > 0 ? n.toLocaleString() : '';
}

/** 0 날짜(레거시의 '값 없음')는 빈 칸으로, 있으면 YYYY-MM-DD 로. */
function dateDraftOf(value: unknown): string {
  return isZeroDate(value) ? '' : String(value).slice(0, 10);
}

/**
 * 수정은 입금건 수정(BankPage 의 toFormState 'edit')처럼 그 건의 값을 그대로
 * 베껴 채운다. 등록은 빈 폼에서 시작하고 등록자만 로그인한 계정 이름을 채운다.
 */
function toCardFormState(row: Row | null, corp: string, defaultUpName: string, mode: 'create' | 'edit'): CardFormState {
  const src = mode === 'edit' ? row : null;
  return {
    corp: src ? String(src.corp ?? corp) : corp,
    card_type: src ? String(src.card_type ?? '') : '',
    use_area: src ? String(src.use_area ?? '0') : '0',
    se_name: src ? String(src.se_name ?? '') : '',
    // 다른 사람 대신 등록하는 경우도 있어 등록자는 수정을 계속 허용한다.
    up_name: src ? String(src.up_name ?? '') : defaultUpName,
    amountDraft: src ? amountDraftOf(src.amount) : '',
    amountUsDraft: src ? amountDraftOf(src.amount_us) : '',
    usRateDraft: src ? amountDraftOf(src.us_rate) : '',
    cardmemo: src ? String(src.cardmemo ?? '') : '',
    // 처리 이력은 수정에서만 다룬다 - 등록 폼에는 이 칸들이 없다.
    re_name: src ? String(src.re_name ?? '') : '',
    report_date: src ? dateDraftOf(src.report_date) : '',
    succ_date: src ? dateDraftOf(src.succ_date) : '',
    memo: src ? String(src.memo ?? '') : '',
  };
}

/**
 * 카드건 추가·수정 모달 — 입금건(BankPage 의 BankFormModal)과 같은 구조·같은 UX다.
 *
 * 다른 점은 컬럼뿐이다: 은행·계좌번호·예금주·계산서 발행 여부(no_tax)는 입금건
 * 전용이라 없고, 대신 card_list 의 카드종류(card_type)·요청부서(use_area)·
 * 요청자(se_name)·달러금액(amount_us)·환율(us_rate)을 받는다. 보고건 등록일·
 * 등록자와 완료일은 그 건 고유의 처리 이력이라, 입금건 수정 모달이 계산서/
 * 보고서/입금완료 칸을 수정에서만 여는 것과 같이 여기서도 수정에서만 보인다.
 */
function CardFormModal({
  mode,
  initial,
  corp,
  corpOptions,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  initial: Row | null;
  /** 목록에서 고른 법인 - 등록은 이 법인으로 저장한다. */
  corp: string;
  corpOptions: { value: string; label: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  // 이 모달은 클릭해서 여는 것이라 이 시점엔 useAuth() 의 계정 정보가 이미 있다
  // (BankPage 의 StatusButton 주석 참고).
  const [form, setForm] = useState<CardFormState>(() =>
    toCardFormState(initial, corp, user?.name ?? user?.userid ?? '', mode),
  );

  const isEdit = mode === 'edit';
  const seq = isEdit && initial ? Number(initial.seq) : 0;
  /** 법인 코드를 목록 필터와 같은 이름으로. 목록에 없는 코드면 코드 그대로 보여준다. */
  const corpLabel = (code: string) => corpOptions.find((o) => o.value === code)?.label ?? code;

  const body: CardEditBody = {
    card_type: form.card_type,
    use_area: Number(form.use_area),
    se_name: form.se_name,
    up_name: form.up_name,
    amount: form.amountDraft,
    amount_us: form.amountUsDraft,
    us_rate: form.usRateDraft,
    cardmemo: form.cardmemo,
    memo: form.memo,
  };

  const save = useMutation({
    mutationFn: () =>
      isEdit
        ? paymentCardApi.update(seq, {
            ...body,
            re_name: form.re_name,
            report_date: form.report_date,
            succ_date: form.succ_date,
          })
        : paymentCardApi.create({ ...body, corp: form.corp }),
    onSuccess: onSaved,
  });

  // 입금건과 같은 수준: 카드(=은행 자리)·등록자·금액이 필수다.
  const valid = form.card_type && form.up_name && form.amountDraft;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">
          <span style={{ color: 'var(--accent)' }}>💳</span>
          {isEdit ? '카드건 수정' : '카드건 추가'}
        </h2>

        {/* 어느 법인으로 저장되는지 폼 맨 위에서 먼저 보여준다 - 등록은 목록에서
            고른 법인으로 들어가고, 수정은 그 건이 등록될 때 정해진 법인을 따른다. */}
        <div className="form-summary">
          <div className="form-summary__corp">관리 법인: {corpLabel(String(initial?.corp ?? corp))}</div>
          <div className="form-summary__sub">
            {isEdit && initial
              ? `접수번호 #${String(initial.seq ?? '')} · 등록일: ${dateOnly(initial.regdate as string)}`
              : '목록에서 고른 법인으로 등록됩니다.'}
          </div>
        </div>

        <form
          // .form-grid(auto-fit 다열 그리드) 대신 .form-stack — 입금건 추가 폼과
          // 같은 이유다(BankPage.tsx 의 주석 참고).
          className="form-stack"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="row-2">
            <Field label="카드">
              <select
                className="input"
                value={form.card_type}
                onChange={(e) => setForm((f) => ({ ...f, card_type: e.target.value }))}
              >
                <option value="">카드 선택</option>
                {Object.entries(CARD_TYPES).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="요청부서">
              <select
                className="input"
                value={form.use_area}
                onChange={(e) => setForm((f) => ({ ...f, use_area: e.target.value }))}
              >
                {Object.entries(USE_AREAS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="row-2">
            <Field label="요청자">
              <input
                className="input"
                value={form.se_name}
                onChange={(e) => setForm((f) => ({ ...f, se_name: e.target.value }))}
              />
            </Field>
            <Field label="등록자">
              <input
                className="input"
                value={form.up_name}
                onChange={(e) => setForm((f) => ({ ...f, up_name: e.target.value }))}
              />
            </Field>
          </div>

          <Field label="결제금액">
            <input
              className="input input--amount"
              value={form.amountDraft}
              onChange={(e) => setForm((f) => ({ ...f, amountDraft: formatAmountDraft(e.target.value) }))}
            />
          </Field>

          <Field label="결제사유">
            <input
              className="input"
              value={form.cardmemo}
              onChange={(e) => setForm((f) => ({ ...f, cardmemo: e.target.value }))}
            />
          </Field>

          {/* 달러 결제 건만 쓰는 칸이다 — 안 채우면 원본처럼 0 으로 저장된다. */}
          <div className="form-divider">달러 결제 (해당 건만)</div>
          <div className="row-2">
            <Field label="결제금액(달러)">
              <input
                className="input input--amount"
                value={form.amountUsDraft}
                onChange={(e) => setForm((f) => ({ ...f, amountUsDraft: formatAmountDraft(e.target.value) }))}
              />
            </Field>
            <Field label="환율">
              <input
                className="input input--amount"
                value={form.usRateDraft}
                onChange={(e) => setForm((f) => ({ ...f, usRateDraft: formatAmountDraft(e.target.value) }))}
              />
            </Field>
          </div>

          {isEdit && (
            <>
              <div className="form-divider">보고건 등록</div>
              <div className="row-2">
                <Field label="보고건 등록자">
                  <input
                    className="input"
                    value={form.re_name}
                    onChange={(e) => setForm((f) => ({ ...f, re_name: e.target.value }))}
                  />
                </Field>
                <Field label="보고건 등록일">
                  <div className="status-editor__row">
                    <input
                      className="input"
                      type="date"
                      value={form.report_date}
                      onChange={(e) => setForm((f) => ({ ...f, report_date: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setForm((f) => ({ ...f, report_date: localDate() }))}
                    >
                      오늘
                    </button>
                  </div>
                </Field>
              </div>

              <div className="form-divider">완료 처리</div>
              <Field label="완료일">
                <div className="status-editor__row">
                  <input
                    className="input"
                    type="date"
                    value={form.succ_date}
                    onChange={(e) => setForm((f) => ({ ...f, succ_date: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setForm((f) => ({ ...f, succ_date: localDate() }))}
                  >
                    오늘
                  </button>
                </div>
              </Field>
            </>
          )}

          <div className="form-divider">비고</div>
          <Field label="비고 (메모)">
            <textarea
              className="input input--area"
              value={form.memo}
              onChange={(e) => setForm((f) => ({ ...f, memo: e.target.value }))}
            />
          </Field>

          {save.isError && (
            <div className="qty-msg qty-msg--error">
              {save.error instanceof ApiError ? save.error.message : '저장에 실패했습니다.'}
            </div>
          )}

          <button type="submit" className="btn btn--primary btn--block" disabled={!valid || save.isPending}>
            {save.isPending ? '저장 중…' : '저장하기'}
          </button>
        </form>
      </div>
    </div>
  );
}

export function CardPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const update = useUrlUpdate(params, setParams);
  const [q, setQ] = useUrlSearch(params, update, 'q');
  const corp = params.get('corp') || 'bnncompany';
  const state = params.get('state') || '0';
  const page = Number(params.get('page') || '1');
  const [formModal, setFormModal] = useState<{ mode: 'create' | 'edit'; row: Row | null } | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<number | null>(null);

  const meta = useQuery({
    queryKey: ['report', 'payment-card', 'meta'],
    queryFn: () => reportsApi.meta('payment-card'),
    staleTime: 5 * 60 * 1000,
  });
  const listParams = useMemo(
    () => ({ q, corp, state, page, perPage: 100, sort: 'regdate', dir: 'DESC' }),
    [q, corp, state, page],
  );
  const list = useQuery({
    queryKey: ['payment-card', 'list', listParams],
    queryFn: () => reportsApi.data('payment-card', listParams),
  });

  function refresh() {
    void qc.invalidateQueries({ queryKey: ['payment-card', 'list'] });
  }

  // 입금건 삭제와 같다 - 한 번 더 확인한 뒤에만 소프트 삭제(stats=1)한다.
  const remove = useMutation({
    mutationFn: (seq: number) => paymentCardApi.remove(seq),
    onSuccess: () => {
      setConfirmingDelete(null);
      refresh();
    },
  });

  const corpOptions = meta.data?.filters.find((filter) => filter.key === 'corp')?.options ?? [
    { value: 'bnncompany', label: '비앤앤 컴퍼니' },
  ];
  const rows: Row[] = list.data?.items ?? [];

  return (
    <section className="payment-card-page">
      <header className="page-head payment-card-head">
        <div>
          <h1 className="page-title">카드결제 관리 <span className="badge badge--danger">관리자 전용</span></h1>
          <p className="page-desc">법인카드 결제 내역과 보고건 등록·완료 상태를 확인합니다.</p>
        </div>
        <div className="page-head__actions">
          <Link className="btn btn--ghost btn--sm" to="/reports/payment-bank">입금건 관리</Link>
          <Link className="btn btn--ghost btn--sm" to="/reports/payment-recurring">정기결제 관리</Link>
        </div>
      </header>

      <div className="filters payment-card-filters">
        <Field label="법인">
          <select className="input" value={corp} onChange={(event) => update({ corp: event.target.value, page: 1 })}>
            {corpOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </Field>
        <Field label="검색">
          <SearchInput value={q} onChange={setQ} placeholder="결제사유 / 결제자 / 금액" />
        </Field>
        <button type="button" className="btn btn--ghost filters__reset"
          onClick={() => setParams(new URLSearchParams())}>초기화</button>
      </div>

      <div className="tab-group" aria-label="처리 상태">
        {STATE_TABS.map((tab) => (
          <button key={tab.value} type="button"
            className={state === tab.value ? 'tab-btn tab-btn--active' : 'tab-btn'}
            onClick={() => update({ state: tab.value, page: 1 })}>{tab.label}</button>
        ))}
      </div>

      <div className="toolbar payment-card-toolbar">
        {/* 위치·크기·스타일 모두 입금건 관리의 '+ 입금건 추가' 버튼과 같다. */}
        <button
          type="button"
          className="btn btn--primary btn--sm"
          style={{ marginLeft: 'auto' }}
          onClick={() => setFormModal({ mode: 'create', row: null })}
        >
          + 카드건 추가
        </button>
      </div>

      {meta.isError ? (
        <ErrorBox error={meta.error} onRetry={() => void meta.refetch()} />
      ) : list.isPending ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyBox>조건에 맞는 카드 결제 내역이 없습니다.</EmptyBox>
      ) : (
        <CardTable
          data={list.data}
          rows={rows}
          page={page}
          onEdit={(row) => setFormModal({ mode: 'edit', row })}
          confirmingDelete={confirmingDelete}
          onAskDelete={setConfirmingDelete}
          onDelete={(seq) => remove.mutate(seq)}
          deleting={remove.isPending}
        />
      )}

      {list.data && list.data.totalPages > 1 && (
        <Pagination page={list.data.page} totalPages={list.data.totalPages} total={list.data.total}
          onChange={(next) => update({ page: next }, false)} />
      )}

      {formModal && (
        <CardFormModal
          mode={formModal.mode}
          initial={formModal.row}
          corp={corp}
          corpOptions={corpOptions}
          onClose={() => setFormModal(null)}
          onSaved={() => {
            // 입금건 추가·수정과 같다 — 모달을 닫고 목록 쿼리만 무효화한다(전체 새로고침 없음).
            setFormModal(null);
            refresh();
          }}
        />
      )}
    </section>
  );
}

function CardTable({
  data,
  rows,
  page,
  onEdit,
  confirmingDelete,
  onAskDelete,
  onDelete,
  deleting,
}: {
  data: ReportData;
  rows: Row[];
  page: number;
  onEdit: (row: Row) => void;
  confirmingDelete: number | null;
  onAskDelete: (seq: number | null) => void;
  onDelete: (seq: number) => void;
  deleting: boolean;
}) {
  return (
    <div className="table-wrap payment-card-table-wrap">
      <table className="table payment-card-table">
        <thead><tr>
          <th>No</th><th>카드 / 요청부서</th><th>요청자</th>
          <th style={{ textAlign: 'right' }}>결제금액</th><th>결제자</th><th>결제사유</th>
          <th>보고건</th><th>완료여부</th><th>등록 / 수정</th><th>메모</th>
          <th className="payment-card-table__manage-head">관리</th>
        </tr></thead>
        <tbody>
          {rows.map((row, index) => {
            const reportPending = isZeroDate(row.report_date);
            const completionPending = isZeroDate(row.succ_date);
            const cardType = CARD_TYPES[String(row.card_type ?? '')] ?? String(row.card_type ?? '-');
            const useArea = USE_AREAS[String(row.use_area ?? '')] ?? `부서코드 ${String(row.use_area ?? '-')}`;
            return (
              <tr key={String(row.seq ?? index)} className={reportPending ? 'payment-card-row--pending' : undefined}>
                <td className="td-muted">{data.total - ((page - 1) * data.perPage + index)}</td>
                <td><div className="payment-card-kind"><strong>{cardType}</strong><span>{useArea}</span></div></td>
                <td>{text(String(row.se_name ?? ''))}</td>
                <td className="payment-card-amount">
                  <strong>{money(row.amount as number)}<small>원</small></strong>
                  {Number(row.amount_us ?? 0) > 0 && <span>{money(row.amount_us as number)} USD</span>}
                </td>
                <td>{text(String(row.up_name ?? ''))}</td>
                <td><span className="payment-card-note" title={String(row.cardmemo ?? '')}>{text(String(row.cardmemo ?? ''))}</span></td>
                <td>
                  {reportPending ? <span className="badge badge--warn">보고 대기</span> : (
                    <div className="payment-card-state payment-card-state--ok"><strong>{dateOnly(String(row.report_date))}</strong><small>{text(String(row.re_name ?? ''))}</small></div>
                  )}
                </td>
                <td>
                  {completionPending ? <span className="badge">처리 예정</span> : (
                    <div className="payment-card-state payment-card-state--ok"><strong>완료</strong><small>{dateOnly(String(row.succ_date))}</small></div>
                  )}
                </td>
                <td className="payment-card-dates">
                  <span>{datetime(String(row.regdate ?? ''))}</span>
                  {!isZeroDate(row.lastupdate) && <small>수정 {datetime(String(row.lastupdate))}</small>}
                </td>
                <td><span className="payment-card-note" title={String(row.memo ?? '')}>{text(String(row.memo ?? ''))}</span></td>
                <td className="payment-card-table__manage-cell">
                  {/* 위치·크기·스타일 모두 입금건 관리의 '수정'·'삭제' 버튼과 같다. */}
                  <div className="payment-card-actions" aria-label="카드건 관리">
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm payment-card-actions__button"
                      onClick={() => onEdit(row)}
                    >
                      수정
                    </button>
                    {confirmingDelete === Number(row.seq) ? (
                      <>
                        <button
                          type="button"
                          className="btn btn--sm payment-card-actions__button payment-card-actions__button--delete-confirm"
                          disabled={deleting}
                          onClick={() => onDelete(Number(row.seq))}
                        >
                          정말 삭제
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm payment-card-actions__button"
                          onClick={() => onAskDelete(null)}
                        >
                          취소
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm payment-card-actions__button payment-card-actions__button--delete"
                        onClick={() => onAskDelete(Number(row.seq))}
                      >
                        삭제
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        {data.totals && <tfoot><tr className="row--total">
          <td colSpan={3} style={{ textAlign: 'right' }}>합계</td>
          <td className="payment-card-amount">
            <strong>{money(data.totals.amount)}<small>원</small></strong>
            {Number(data.totals.amount_us ?? 0) > 0 && <span>{money(data.totals.amount_us)} USD</span>}
          </td>
          <td colSpan={7} />
        </tr></tfoot>}
      </table>
    </div>
  );
}
